import { useCallback, useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LuxPlusLabel from 'LuxKit/Label/LuxPlusLabel'
import Tooltip from 'LuxKit/Tooltip'
import type { ModalFooterOnClickHandler } from 'LuxKit/Modal/ModalFooter'
import LuxPlusLabelWithModal from 'luxPlus/components/LuxPlusLabelWithModal'
import useLuxPlusLandingActions from 'luxPlus/hooks/useLuxPlusLandingActions'
import ContactContext from 'contexts/contactContext'

const MEMBER_PRICE_OFFER_CRUISE_TYPE = 'member-price-offer-cruise' as const

const CRUISE_LUX_PLUS_PRIORITY_BENEFIT_TYPES: Array<App.LuxPlusBenefitType> = [
  'discounted_cruise',
  'cruise_inclusions',
]

export type CruiseLuxPlusLabelProps = {
  isModalEnabled?: boolean
  offer?: App.CruiseOffer
}

function CruiseLuxPlusLabel({ isModalEnabled = true, offer }: CruiseLuxPlusLabelProps) {
  const intl = useIntl()
  const { handleBecomeMember } = useLuxPlusLandingActions()
  const currentContact = useContext(ContactContext)
  const { number: supportPhone, humanReadable: supportPhoneLabel } = currentContact.defaultContact

  const handleJoinLuxPlusMembership = useCallback<ModalFooterOnClickHandler>(
    (_e, resolve) => {
      resolve()
      handleBecomeMember()
    },
    [handleBecomeMember],
  )

  const handleCallCruiseSupport = useCallback<ModalFooterOnClickHandler>(
    (_e, resolve) => {
      if (supportPhone) {
        window.location.assign(`tel:${supportPhone}`)
      }
      resolve()
    },
    [supportPhone],
  )

  const cruiseCallSecondaryAction = useMemo(
    () =>
      supportPhone
        ? {
            secondaryActionText: (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  columnGap: 6,
                }}
              >
                <LinePhoneIcon />
                <FormattedMessage
                  id="Y86IWbONeZPFFfPd9r7n"
                  defaultMessage="Call {phoneNumber}"
                  description="Action button label to call support with phone number"
                  values={{ phoneNumber: supportPhoneLabel }}
                />
              </span>
            ),
            onSecondaryActionClick: handleCallCruiseSupport,
          }
        : undefined,
    [supportPhone, supportPhoneLabel, handleCallCruiseSupport],
  )

  const memberDiscountTooltipDescription = intl.formatMessage({
    id: 'lZ4CUHlo5OuvP0hKe2Mb',
    defaultMessage: 'Go through the booking flow to unlock exclusive LuxPlus+ price',
    description:
      'Tooltip on the cruise member-price LuxPlus label; explains that calling unlocks membership discount for the booking',
  })

  if (isModalEnabled) {
    return (
      <Tooltip description={memberDiscountTooltipDescription} placement="top">
        <LuxPlusLabelWithModal
          type={MEMBER_PRICE_OFFER_CRUISE_TYPE}
          offer={offer}
          hideSpecialLabel
          primaryActionText={intl.formatMessage({
            id: 'UzhEm0puAIDh0IlibYoA',
            defaultMessage: 'Join now',
            description: 'Primary action button text to join LuxPlus+ membership from the cruise overview pane',
          })}
          onPrimaryActionClick={handleJoinLuxPlusMembership}
          priorityBenefitTypes={CRUISE_LUX_PLUS_PRIORITY_BENEFIT_TYPES}
          {...(cruiseCallSecondaryAction ?? {})}
        />
      </Tooltip>
    )
  }

  return (
    <Tooltip description={memberDiscountTooltipDescription} placement="top">
      <LuxPlusLabel type={MEMBER_PRICE_OFFER_CRUISE_TYPE} />
    </Tooltip>
  )
}

export default CruiseLuxPlusLabel
